import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import SiteDialog from "../../components/SiteDialog";
import AppPagination from "../../components/AppPagination";
import theme from "../../utils/theme";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { CategoryType } from "../../types/types";
import { useLocation } from "react-router-dom";
import Loading from "../../components/Loading";
import CreateCategory from "../../components/category/CreateCategory";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UpdateCategory from "../../components/category/UpdateCategory";
import { useCategoryRequest } from "../../hooks/categoryRequest";
import { IMG_BASE_URL } from "../../utils/config";
import AlertDialog from '../../components/AlertDialog';
import SearchInput from '../../components/filters/SearchInput';
import RefreshButton from '../../components/filters/RefreshButton';
import { toggleActive } from '../../slices/categorySlice';

const CategoriesList: React.FC = () => {
  const { search } = useLocation();
  const { loading, data: categoriesData } = useAppSelector(state => state.category);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(0);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [updateOpen, setUpdateOpen] = useState<boolean>(false);
  const [category, setCategory] = useState<CategoryType | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [ searchText,setSearchText ] = useState<string>("")
  const dispatch = useAppDispatch()
  const categoryRequest = useCategoryRequest();

  const fetchData = useCallback(async () => {
    try {
      await categoryRequest({ method: 'GET', data: null, slug: null, query: null });
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [categoryRequest]);

  const handleEdit = (category: CategoryType) => {
    setCategory(category);
    setUpdateOpen(true);
  };

  const handleDelete = (toDeleteSlug: string) => {
    setSlug(toDeleteSlug);
    setShowAlert(true);
  };

  const handleCancel = () => {
    setShowAlert(false);
  };

  const handleConfirm = useCallback(async () => {
    if (slug) {
      try {
        await categoryRequest({ method: 'DELETE', data: null, slug, query: null });
        setShowAlert(false); 
        fetchData(); 
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  }, [categoryRequest, fetchData, slug]);

  const handleChangeActive = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, slug: string ) => {
      try {
        await dispatch(toggleActive({ data: { isActive: event.target.checked }, slug }));
        await fetchData();
      } catch (error) {
        console.error('Failed to update carousel:', error);
      }
    },
    [dispatch, fetchData]
  );

  useEffect(() => {
    if (categoriesData) {
      const { meta } = categoriesData;
      setLastPage(Number(meta?.lastPage) || 1);
      setCurrentPage(Number(meta?.currentPage));
      setPerPage(Number(meta?.perPage));
      setCategories(categoriesData.data);
    }
  }, [categoriesData]);

  useEffect(() => {
    fetchData();
  }, [search, fetchData]);

  return (
    <Grid container columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: '8px' }}>
          {loading && <Loading />}
          <AlertDialog
            toggle={showAlert}
            setToggle={setShowAlert}
            cancel={handleCancel}
            confirm={handleConfirm}
            title={"Are you sure?"}
            content={"You want to delete this category."}
            type="error"
          />
          {category && (
            <SiteDialog
              open={updateOpen}
              setOpen={setUpdateOpen}
              dialogChildren={<UpdateCategory categoryData={category} setDialogOpen={setUpdateOpen} />}
            />
          )}
          <Typography variant="h5" sx={{ mb: 3 }}>Categories List</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 5 }}>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>Add New</Button>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <SearchInput
                searchText={searchText}
                setSearchText={setSearchText}
                placeholderText='Search by title'
              />
              <Box sx={{ mr: 3 }}></Box>
              <RefreshButton/>
            </Box>
            <SiteDialog
              open={createOpen}
              setOpen={setCreateOpen}
              dialogChildren={<CreateCategory setDialogOpen={setCreateOpen} />}
            />
          </Box>
          <TableContainer sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: '10px' }}>
            <Table sx={{ minWidth: 300 }} aria-label="caption table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="left">Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Image</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Active</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length > 0 ? categories.map((category: CategoryType, index: number) => (
                  <TableRow key={category.id}>
                    <TableCell align="center">{((currentPage - 1) * perPage) + (index + 1)}</TableCell>
                    <TableCell align="left">{category.name}</TableCell>
                    <TableCell align="center">
                      {
                        category.link ?
                        <img
                          src={`${IMG_BASE_URL}${category.link}`}
                          style={{ width: '120px' }}
                          alt={category.photoFilename ? category.photoFilename : "Image not found"}
                          loading="lazy"
                        />
                        :
                        <Typography>No Image</Typography>
                      }
                    </TableCell>
                    <TableCell align='center'>
                      <Switch
                        checked={category.isActive}
                        onChange={(event) => handleChangeActive(event, category.slug)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Button onClick={() => handleEdit(category)} sx={{ mr: 2 }} variant="contained" size="small" color="warning">
                        <EditIcon />
                      </Button>
                      <Button onClick={() => handleDelete(category.slug)} variant="contained" size="small" color="error">
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No categories found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <AppPagination pageCount={Number(lastPage)} />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CategoriesList;
